import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing, type TemplateResult } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { MatchingWeekSettings } from '../../../db/settings.js';
import {
  applyMatchingWeekColors,
  MW_DEFAULT_PRIMARY_COLOR,
  MW_DEFAULT_PRIMARY_CONTENT_COLOR,
} from '../../theme-colors.js';

const ColorRow = ({
  label,
  description,
  value,
  defaultValue,
  disabled,
  onChange,
}: {
  label: string | TemplateResult;
  description: string | TemplateResult;
  value: string | null | undefined;
  defaultValue: string;
  disabled: boolean;
  onChange: (value: string | null) => void;
}) => html`
  <div class="form-control">
    <label class="label">
      <span class="label-text font-semibold">${label}</span>
    </label>
    <p class="text-sm text-base-content/70 mb-2">${description}</p>
    <div class="flex items-center gap-3">
      <input
        type="color"
        class="w-12 h-12 cursor-pointer rounded border border-base-300 bg-base-100"
        .value=${value || defaultValue}
        ?disabled=${disabled}
        @input=${(e: InputEvent) => onChange((e.target as HTMLInputElement).value)}
      />
      <span class="font-mono text-sm">${value || defaultValue}</span>
      ${
        value && value !== defaultValue
          ? html`<button class="btn btn-ghost btn-sm" ?disabled=${disabled} @click=${() => onChange(null)}>
              <span class="icon-[material-symbols--restart-alt]"></span>
              ${m.admin_design_settings_reset()}
            </button>`
          : nothing
      }
    </div>
  </div>
`;

export const AdminDesignSettings = stateful(() => {
  const [saving, setSaving] = useState(false, []);
  const [saveSuccess, setSaveSuccess] = useState(false, []);
  const [saveError, setSaveError] = useState<string | null>(null, []);
  // Entity mutations don't trigger a re-render on their own.
  const [renderTick, setRenderTick] = useState(0, []);

  const settingsQuery = useQuery(
    () =>
      subscribeMatchingWeekSettings()({
        _id: 1,
        primaryColor: 1,
        primaryContentColor: 1,
      }),
    [],
  );

  const handleSave = async () => {
    if (!settingsQuery.value) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await (settingsQuery.value as MatchingWeekSettings).save();
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(e?.message ?? 'Saving failed');
    } finally {
      setSaving(false);
    }
  };

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center">
      <div class="max-w-4xl w-full space-y-6">
        ${
          settingsQuery.loading
            ? html`<div class="flex justify-center py-8"><span class="loading loading-spinner loading-lg"></span></div>`
            : nothing
        }
        ${
          saveSuccess
            ? html`<div class="alert alert-success">
                <span class="icon-[material-symbols--check-circle]"></span
                ><span>${m.admin_design_settings_saved()}</span>
              </div>`
            : nothing
        }
        ${
          saveError
            ? html`<div class="alert alert-error">
                <span class="icon-[material-symbols--error]"></span><span>${saveError}</span>
              </div>`
            : nothing
        }
        ${
          settingsQuery.value
            ? html`
                <div class="card bg-base-100 shadow-xl">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-1">${m.admin_design_settings_title()}</h2>
                    <p class="text-sm text-base-content/70 mb-4">${m.admin_design_settings_desc()}</p>

                    <div class="space-y-6">
                      ${ColorRow({
                        label: m.admin_design_settings_primary_label(),
                        description: m.admin_design_settings_primary_desc(),
                        value: settingsQuery.value.primaryColor,
                        defaultValue: MW_DEFAULT_PRIMARY_COLOR,
                        disabled: saving,
                        onChange: value => {
                          if (!settingsQuery.value) return;
                          settingsQuery.value.primaryColor = value;
                          // Live preview; reverts on reload if not saved.
                          applyMatchingWeekColors(settingsQuery.value);
                          setRenderTick(renderTick + 1);
                        },
                      })}
                      ${ColorRow({
                        label: m.admin_design_settings_primary_content_label(),
                        description: m.admin_design_settings_primary_content_desc(),
                        value: settingsQuery.value.primaryContentColor,
                        defaultValue: MW_DEFAULT_PRIMARY_CONTENT_COLOR,
                        disabled: saving,
                        onChange: value => {
                          if (!settingsQuery.value) return;
                          settingsQuery.value.primaryContentColor = value;
                          applyMatchingWeekColors(settingsQuery.value);
                          setRenderTick(renderTick + 1);
                        },
                      })}
                    </div>

                    <div class="mt-6 p-4 rounded-box border border-base-300 flex flex-wrap items-center gap-3">
                      <span class="text-sm text-base-content/70">${m.admin_design_settings_preview()}</span>
                      <button class="btn btn-primary">${m.admin_design_settings_preview_button()}</button>
                    </div>

                    <div class="flex justify-end mt-4">
                      <button class="btn btn-primary" ?disabled=${saving} @click=${handleSave}>
                        <span class="icon-[material-symbols--save]"></span>
                        ${m.admin_design_settings_save()}
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
});
